import * as React from "react";
import {
  Button,
  Checkbox,
  MessageBar,
  MessageBarBody,
  Spinner,
  Text,
  Tooltip,
  makeStyles,
  tokens,
} from "@fluentui/react-components";
import {
  ChevronDown20Regular,
  ChevronRight20Regular,
  CloudArrowUp24Regular,
  DismissCircle20Regular,
} from "@fluentui/react-icons";
import type { UploadState } from "../../types/sharepoint";
import type { UploadOptions as UploadOptionsType } from "../../hooks/useSharePoint";
import type { SelectedMailItem } from "../../hooks/useSelectedMailItems";

const useStyles = makeStyles({
  root: { display: "flex", flexDirection: "column", gap: tokens.spacingVerticalS },

  mutedText: {
    color: tokens.colorNeutralForeground3,
  },

  optionsToggle: {
    alignSelf: "flex-start",
  },

  checkboxes: {
    backgroundColor: tokens.colorNeutralBackground2,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusMedium,
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalXXS,
    padding: tokens.spacingHorizontalS,
  },

  uploadButton: { width: "100%" },
});

interface UploadOptionsProps {
  selectedFolderName?: string;
  selectedEmails?: SelectedMailItem[];
  upload: UploadState;
  onUpload: (options: UploadOptionsType, selectedEmails?: SelectedMailItem[]) => void;
  onClearStatus: () => void;
}

export const UploadOptions: React.FC<UploadOptionsProps> = ({
  selectedFolderName,
  selectedEmails = [],
  upload,
  onUpload,
  onClearStatus,
}) => {
  const styles = useStyles();
  const [skipDuplicates, setSkipDuplicates] = React.useState(true);
  const [includeAttachments, setIncludeAttachments] = React.useState(true);
  const [overwrite, setOverwrite] = React.useState(false);
  const [showOptions, setShowOptions] = React.useState(false);

  const handleUpload = () =>
    onUpload({ skipDuplicates, includeAttachments, overwrite }, selectedEmails);

  const emailCount = selectedEmails.length;
  const tooltipContent = selectedFolderName
    ? `Upload to "${selectedFolderName}"`
    : "Choose a folder to continue";

  const uploadButtonLabel = !selectedFolderName
    ? "Choose a folder to continue"
    : upload.loading
      ? "Creating upload job..."
      : emailCount > 0
        ? `Upload ${emailCount} email${emailCount === 1 ? "" : "s"}`
        : "Upload email";

  return (
    <div className={styles.root}>
      <Text size={200} className={styles.mutedText}>
        Uploads run in the background, so progress will continue even if the taskpane closes.
      </Text>

      <Tooltip content={tooltipContent} relationship="label">
        <Button
          appearance="primary"
          icon={upload.loading ? <Spinner size="tiny" /> : <CloudArrowUp24Regular fontSize={18} />}
          className={styles.uploadButton}
          size="small"
          disabled={upload.loading || !selectedFolderName}
          onClick={handleUpload}
        >
          {uploadButtonLabel}
        </Button>
      </Tooltip>

      <Button
        appearance="transparent"
        size="small"
        className={styles.optionsToggle}
        icon={showOptions ? <ChevronDown20Regular /> : <ChevronRight20Regular />}
        onClick={() => setShowOptions((value) => !value)}
      >
        Options
      </Button>

      {showOptions && (
        <div className={styles.checkboxes}>
          <Checkbox
            label="Skip duplicate emails"
            checked={skipDuplicates}
            onChange={(_, d) => setSkipDuplicates(!!d.checked)}
            size="medium"
          />
          <Checkbox
            label="Include attachments"
            checked={includeAttachments}
            onChange={(_, d) => setIncludeAttachments(!!d.checked)}
            size="medium"
          />
          <Checkbox
            label="Overwrite existing files"
            checked={overwrite}
            onChange={(_, d) => setOverwrite(!!d.checked)}
            size="medium"
          />
        </div>
      )}

      {upload.error && (
        <MessageBar intent="error">
          <MessageBarBody>{upload.error}</MessageBarBody>
          <Button
            appearance="transparent"
            icon={<DismissCircle20Regular />}
            size="small"
            onClick={onClearStatus}
            aria-label="Dismiss error"
          />
        </MessageBar>
      )}

      {upload.success && (
        <MessageBar intent="success">
          <MessageBarBody>{upload.success}</MessageBarBody>
          <Button
            appearance="transparent"
            icon={<DismissCircle20Regular />}
            size="small"
            onClick={onClearStatus}
            aria-label="Dismiss success"
          />
        </MessageBar>
      )}
    </div>
  );
};
